import { cx } from "@emotion/css";
import { Divider } from "@mui/material";
import { useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useTranslation } from "react-i18next";
import { getAppConfig } from "src/hooks/useAppConfig";
import { useFontSize } from "src/hooks/useFontSize";
import { useNoCopy } from "src/hooks/useNoCopy";
import { isStaff } from "src/hooks/useUser";

import isMobile from "../../../helpers/isMobile";
import Gallery from "../../NewComponents/Images/Gallery";
import QuestionLevelChip from "../QuestionLevelChip";

interface QuestionIdComponentProps {
	questionId: string | number;
}

const QuestionIdComponent = ({ questionId }: QuestionIdComponentProps) => {
	const [copied, setCopied] = useState(false);

	const onSetCopied = () => {
		setCopied(true);
		setTimeout(() => {
			setCopied(false);
		}, 500);
	};

	return (
		<CopyToClipboard text={questionId} onCopy={onSetCopied}>
			<span className="size-12-light">
				{copied ? (
					<span className="text-success">Skopiowano</span>
				) : (
					<span className="text-grey link-clear">
						{"(Id pytania:"}
						<label className="text-primary link-clear">{questionId}</label>
						{")"}
					</span>
				)}
			</span>
		</CopyToClipboard>
	);
};

interface QuestionData {
	id: string | number;
	pytanie: string;
	images?: Array<{ url: string; [key: string]: unknown }>;
	isInBazaPytan?: boolean;
	pytanie_id_in_cem_pdf?: string | number;
	dzial?: string;
	termin: string;
	isCanceled: boolean;
}

interface QuestionLevelStats {
	level: "hard" | "medium" | "easy";
	percentOfWrongAnswers: number;
	percentOfCorrectAnswers: number;
}

interface QuestionDetails {
	stats?: QuestionLevelStats;
	visibility?: { ai: boolean };
	hasAiChat?: boolean;
}

interface QuestionState {
	settings?: {
		questionLevel?: boolean;
	};
}

interface QuestionProps {
	question: QuestionData;
	questionDetails: QuestionDetails;
	isAnswerSet: boolean;
	state?: QuestionState;
	showImages?: boolean;
	headerClassName?: string;
	questionClassName?: string;
}

function Question(props: QuestionProps) {
	const appConfig = getAppConfig();
	const { onCopy, noCopyClassName } = useNoCopy({
		config: appConfig.copyContentSettings.question,
	});
	const { fontSizeClass, getFontSizeClass } = useFontSize();
	const { t } = useTranslation();
	const { showImages = true, state: { settings } = {} } = props;

	const renderQuestionId = () => {
		if (!isStaff()) {
			return null;
		}

		return <QuestionIdComponent questionId={props.question.id} />;
	};

	const renderQuestionLevel = () => {
		if (!settings?.questionLevel || !props.questionDetails.stats) {
			return null;
		}

		return <QuestionLevelChip details={props.questionDetails.stats} />;
	};

	const renderImagesSections = () => {
		if (
			!props.question.images ||
			props.question.images.length === 0 ||
			!showImages
		) {
			return null;
		}

		return (
			<div>
				<Divider />
				<span className={getFontSizeClass(-1)}>{t("exam.images.title")}</span>
				<Gallery
					images={props.question.images}
					maxImages={isMobile() ? 5 : null}
					onDelete={undefined}
				/>
			</div>
		);
	};

	const isInCem = props.question.isInBazaPytan
		? `, ${t("exam.cemDatabase")} #${props.question.pytanie_id_in_cem_pdf}`
		: "";

	const questionInfo = `${
		props.question.dzial ? `${props.question.dzial}, ` : ""
	}${props.question.termin}${isInCem}`;

	return (
		<div className="flex flex-col">
			<div className="flex items-center gap-4">
				<label
					className={cx(
						"text-grey",
						getFontSizeClass(-1),
						props.headerClassName,
					)}
				>
					{questionInfo}
				</label>
				{renderQuestionLevel()}
				{renderQuestionId()}
			</div>
			<p
				className={cx(
					"m-0 question",
					noCopyClassName,
					fontSizeClass,
					props.questionClassName,
				)}
				id="question"
				dangerouslySetInnerHTML={{ __html: props.question.pytanie }}
				onCopy={(e) =>
					onCopy(e, { questionId: props.question.id, type: "Question" })
				}
			/>
			{renderImagesSections()}
		</div>
	);
}

export default Question;
